import { useApiOperation } from "src/api/hooks";

import GoToListButton from "src/components/GoToListButton";

import { useQuery } from "@tanstack/react-query";
import { isArray } from "lodash";
import moment from "moment";
import { Row } from "react-table";
import { getHistoryListAsync } from "src/api/history/history-api";
import { HistoryUpdate, HistoryUpdateListParams } from "src/api/history/history-types";
import { Order } from "src/api/public-types";
import { BaseButton, BaseTable, BaseTooltip } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useModal } from "src/recoil/modalState/hook";
import { ViewYmdFormat } from "src/utils";
import HistoryDetailModal from "./HistoryDetailModal";
import { KeyNamesMap, KeyNamesMapKey } from "./libs";

/**
 * 출입그룹 변경내역(AL006 파트너 / AL007 장치) 감사 로그 종류 코드
 * - 경로 정책: access-groups/{accessGroupId}/{logHash}
 * - 변경 전/후 JSON Patch 가 아니라 추가·삭제된 대상 객체만 내려오므로 변경내용 상세를 제공하지 않는다
 */
const ACCESS_GROUP_CATEGORIES = ["AL006", "AL007"];

export const HistoryDetailColumns = [
  {
    Header: "변경일시",
    accessor: "revisionTime",
    width: 220,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      return <div>{row?.original?.revisionTime ? moment(row?.original?.revisionTime).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}</div>;
    },
  },

  {
    Header: "카테고리",
    accessor: "category",
    width: 120,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      const category = row?.original?.category;

      return <div>{category}</div>;
    },
  },
  {
    Header: "대상경로",
    accessor: "path",
    width: 350,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      const messages = row?.original?.message?.data?.data ?? [];

      if (row?.original?.message?.type?.includes("C")) {
        ///AL202-C01
        //초기 생성시에 상세 데이터 보여주지 않음
        return <div className="flex-start w-100">-</div>;
      }

      return (
        <div className="flex-start w-100">
          <BaseTooltip
            contents={
              messages.length > 0
                ? (messages?.map((item, index) => {
                    const matchedPathName =
                      item.path
                        ?.split("/")
                        ?.map((pathKey: string) => {
                          if (!isNaN(Number(pathKey))) return pathKey;
                          const matchedKeyName =
                            KeyNamesMap[row?.original.category as KeyNamesMapKey]?.[pathKey as keyof (typeof KeyNamesMap)[KeyNamesMapKey]];
                          return matchedKeyName || pathKey;
                        })
                        ?.join("/") || "-";

                    return <div key={index}>{"•" + matchedPathName || "-"}</div>;
                  }) as any as string)
                : "-"
            }
          ></BaseTooltip>
        </div>
      );
    },
  },
  {
    Header: "메시지",
    accessor: "message",
    width: 350,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      const messageData = row?.original?.message;
      const supplyTypeMap = {
        RENTAL: "임대료",
        MNT: "관리비",
      };

      const makeMessageText = () => {
        if (messageData?.data?.error && messageData?.type === "AL999-E01") {
          return `[Error] 요청 할 수 있는 로그 데이터 사이즈는 ${messageData?.data?.error?.limitMessageDataBytes} Bytes 입니다(요청: ${messageData?.data?.error?.originalCategory}, ${messageData?.data?.error?.originalMessageType}, ${messageData?.data?.error?.originalMessageDataBytes} Bytes)`;
        }

        if (messageData?.type === "AL141-C001") {
          return `${supplyTypeMap[messageData?.data?.supplyType as keyof typeof supplyTypeMap] || ""} 스케줄정보(providerId:${
            messageData?.data?.providerId ?? "-"
          })가 등록되었습니다.`;
        }

        if (messageData?.type === "AL141-U001") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }

          return `${supplyTypeMap[messageData?.data?.supplyType as keyof typeof supplyTypeMap] || ""} 스케줄정보(providerId:${
            messageData?.data?.providerId ?? "-"
          })가 수정되었습니다.`;
        }

        if (messageData?.type === "AL101-C001") {
          return `정산 정보가 등록되었습니다.`;
        }

        if (messageData?.type === "AL101-U001") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `정산 정보가 수정되었습니다.`;
        }

        if (messageData?.type === "AL115-C001") {
          return `계약그룹이 등록되었습니다.`;
        }

        if (messageData?.type === "AL115-U001") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `계약그룹이 수정되었습니다.`;
        }

        if (messageData?.type === "AL131-C001") {
          return `계약 정보가 등록되었습니다.`;
        }

        if (messageData?.type === "AL131-U001") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `계약 정보가 수정되었습니다.`;
        }

        if (messageData?.type === "AL201-C01") {
          return `건물(${messageData.data.id})이 등록되었습니다.`;
        }

        if (messageData?.type === "AL201-U01") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `건물(${messageData.data.id})이 변경되었습니다.`;
        }

        if (messageData?.type === "AL201-D01") {
          return `건물(${messageData.data.id})이 삭제되었습니다.`;
        }

        if (messageData?.type === "AL202-C01") {
          return `상품(${messageData.data.id})이 등록되었습니다.`;
        }

        if (messageData?.type === "AL202-U01") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `상품(${messageData.data.id})이 변경되었습니다.`;
        }

        if (messageData?.type === "AL202-D01") {
          return `상품(${messageData.data.id})이 삭제되었습니다.`;
        }

        // 공용공간 요금(AL301): priceRuleId 추출
        // - 경로 정책: price-rules/{priceRuleId}/{logHash}
        // - 등록(C01)은 message.data.id, 수정/삭제(U01/D01)는 JSON Patch 배열이라 id가 없으므로 경로에서 우선 추출
        if (messageData?.type?.startsWith("AL301")) {
          const pathSegments = row?.original?.path?.split("/") ?? [];
          const priceRuleIndex = pathSegments.indexOf("price-rules");
          const priceRuleIdFromPath = priceRuleIndex >= 0 ? pathSegments[priceRuleIndex + 1] : undefined;
          const priceRuleId = priceRuleIdFromPath ?? messageData?.data?.id ?? "-";

          if (messageData?.type === "AL301-C01") {
            return `공용공간 요금(${priceRuleId})이 등록 되었습니다.`;
          }

          if (messageData?.type === "AL301-U01") {
            if (messageData.data.data?.length === 0) {
              return "변경된 내용이 없습니다.";
            }
            return `공용공간 요금(${priceRuleId})이 변경 되었습니다.`;
          }

          if (messageData?.type === "AL301-D01") {
            return `공용공간 요금(${priceRuleId})이 삭제 되었습니다.`;
          }
        }

        if (messageData?.type === "AL121-C001") {
          return `이용자 정보가 등록되었습니다.`;
        }

        if (messageData?.type === "AL121-U001") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `이용자 정보가 변경되었습니다.`;
        }

        if (messageData?.type === "AL122-C001") {
          return `방문자그룹 정보가 등록되었습니다.`;
        }

        if (messageData?.type === "AL122-U001") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `방문자그룹 정보가 변경되었습니다.`;
        }

        if (messageData?.type === "AL132-C001") {
          console.log("messageData", messageData);
          return `이용신청 정보 정보가 등록되었습니다.`;
        }
        if (messageData?.type === "AL132-U001") {
          return `이용신청 정보가 변경되었습니다.`;
        }

        // VOC/고객문의(AL401): questionId 추출
        // - 등록(C01)은 id가 data.data 내부에 중첩(data.data.id), 수정(U01)은 변경 배열이라 id가 없음
        // - 모든 케이스에 공통인 경로 정책(cs-questions/{questionId}/{logHash})에서 우선 추출하고, 없으면 중첩 id로 폴백
        if (messageData?.type?.startsWith("AL401")) {
          const pathSegments = row?.original?.path?.split("/") ?? [];
          const questionIndex = pathSegments.findIndex((segment) => segment === "cs-questions" || segment === "cs-qusetions"); // 문서상 cs-qusetions 오타 대응
          const questionIdFromPath = questionIndex >= 0 ? pathSegments[questionIndex + 1] : undefined;
          const questionIdFromData = !isArray(messageData?.data?.data) ? (messageData?.data?.data as unknown as { id?: number })?.id : undefined;
          const questionId = questionIdFromPath ?? questionIdFromData ?? "-";

          if (messageData?.type === "AL401-C01") {
            return `VOC/고객문의(${questionId})가 등록 되었습니다.`;
          }

          if (messageData?.type === "AL401-U01") {
            if (messageData.data.data?.length === 0) {
              return "변경된 내용이 없습니다.";
            }
            return `VOC/고객문의(${questionId})가 변경 되었습니다.`;
          }
        }

        // VoC 항목(AL402): csItemId 추출
        // - 등록(C01)은 id가 data.data 내부에 중첩(data.data.id), 수정/삭제(U01/D01)는 변경 배열이라 id가 없음
        // - 모든 케이스에 공통인 경로 정책(cs-items/{csItemId}/{logHash})에서 우선 추출하고, 없으면 중첩 id로 폴백
        if (messageData?.type?.startsWith("AL402")) {
          const pathSegments = row?.original?.path?.split("/") ?? [];
          const csItemIndex = pathSegments.indexOf("cs-items");
          const csItemIdFromPath = csItemIndex >= 0 ? pathSegments[csItemIndex + 1] : undefined;
          const csItemIdFromData = !isArray(messageData?.data?.data) ? (messageData?.data?.data as unknown as { id?: number })?.id : undefined;
          const csItemId = csItemIdFromPath ?? csItemIdFromData ?? "-";

          if (messageData?.type === "AL402-C01") {
            return `VoC 항목(${csItemId})이 등록 되었습니다.`;
          }

          if (messageData?.type === "AL402-U01") {
            if (messageData.data.data?.length === 0) {
              return "변경된 내용이 없습니다.";
            }
            return `VoC 항목(${csItemId})이 변경 되었습니다.`;
          }

          if (messageData?.type === "AL402-D01") {
            return `VoC 항목(${csItemId})이 삭제 되었습니다.`;
          }
        }

        // 이용안내 항목(AL403): userGuideId 추출
        // - 등록(C01)은 id가 data.data 내부에 중첩(data.data.id), 수정/삭제(U01/D01)는 변경 배열이라 id가 없음
        // - 모든 케이스에 공통인 경로 정책(user-guides/{userGuideId}/{logHash})에서 우선 추출하고, 없으면 중첩 id로 폴백
        if (messageData?.type?.startsWith("AL403")) {
          const pathSegments = row?.original?.path?.split("/") ?? [];
          const userGuideIndex = pathSegments.indexOf("user-guides");
          const userGuideIdFromPath = userGuideIndex >= 0 ? pathSegments[userGuideIndex + 1] : undefined;
          const userGuideIdFromData = !isArray(messageData?.data?.data) ? (messageData?.data?.data as unknown as { id?: number })?.id : undefined;
          const userGuideId = userGuideIdFromPath ?? userGuideIdFromData ?? "-";

          if (messageData?.type === "AL403-C01") {
            return `이용안내 항목(${userGuideId})이 등록 되었습니다.`;
          }

          if (messageData?.type === "AL403-U01") {
            if (messageData.data.data?.length === 0) {
              return "변경된 내용이 없습니다.";
            }
            return `이용안내 항목(${userGuideId})이 변경 되었습니다.`;
          }

          if (messageData?.type === "AL403-D01") {
            return `이용안내 항목(${userGuideId})이 삭제 되었습니다.`;
          }
        }

        // 출입그룹 > 파트너 추가/삭제(AL006)
        if (messageData?.type === "AL006-A01" || messageData?.type === "AL006-R01") {
          const partner = messageData?.data?.partner;
          const action = messageData?.type === "AL006-A01" ? "추가" : "삭제";
          return `${partner?.name ?? "-"}(${partner?.code ?? "-"}) 파트너가 ${action} 되었습니다.`;
        }

        // 출입그룹 > 장치 추가/삭제(AL007)
        if (messageData?.type === "AL007-A01" || messageData?.type === "AL007-R01") {
          const accessDevice = messageData?.data?.accessDevice;
          const action = messageData?.type === "AL007-A01" ? "추가" : "삭제";
          return `${accessDevice?.deviceName ?? "-"}(${accessDevice?.externalDeviceId ?? "-"}) 장치가 ${action} 되었습니다.`;
        }
        // 계약자(AL501)
        // - 경로 정책: organizations/{organizationId}/{logHash}
        // - $.id(계약자 ID)는 등록/수정/삭제 모두 발행되므로 message.data.id 를 그대로 사용한다
        if (messageData?.type === "AL501-C01") {
          return `계약자(${messageData.data.id})가 등록 되었습니다.`;
        }

        if (messageData?.type === "AL501-U01") {
          if (messageData.data.data?.length === 0) {
            return "변경된 내용이 없습니다.";
          }
          return `계약자(${messageData.data.id})가 변경 되었습니다.`;
        }

        if (messageData?.type === "AL501-D01") {
          return `계약자(${messageData.data.id})가 삭제 되었습니다.`;
        }

        if (!isArray(messageData?.data?.data) && messageData?.type?.includes("C")) {
          ///AL202-C01
          //초기 생성시에 상세 데이터 보여주지 않음
          return "항목이 성공적으로 생성되었습니다";
        }

        return "-";
      };

      return (
        <div className="flex-start w-100">
          <BaseTooltip contents={makeMessageText()}></BaseTooltip>
        </div>
      );
    },
  },
  {
    Header: "변경내용",
    accessor: "setting",
    width: 120,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      const { setAbstractModalZ1 } = useModal();

      if (row?.original?.message?.type.includes("C")) {
        //초기 생성시에 상세 데이터 보여주지 않음
        return "-";
      }

      // 출입그룹(AL006/AL007)은 변경 전/후 비교 데이터가 없어 메시지로만 노출한다
      if (ACCESS_GROUP_CATEGORIES.includes(row?.original?.category)) {
        return "-";
      }

      return (
        <div>
          <BaseButton
            title="상세보기"
            className="color-white"
            onClick={() =>
              setAbstractModalZ1({
                isOpen: true,
                size: "xlarge",
                children: <HistoryDetailModal historyUpdate={row.original}></HistoryDetailModal>,
              })
            }
          ></BaseButton>
        </div>
      );
    },
  },
  {
    Header: "사용자타입",
    accessor: "userType",
    width: 120,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      return <BaseTooltip contents={row?.original?.user?.type ?? "-"}></BaseTooltip>;
    },
  },
  {
    Header: "변경자",
    accessor: "displayName",
    width: 120,
    Cell: ({ row }: { row: Row<HistoryUpdate> }) => {
      if (row?.original?.user?.type === "TAAP") {
        return (
          <BaseTooltip contents={row?.original?.user?.id ?? "-"}>
            <span>{row?.original?.user?.id || "-"}</span>
          </BaseTooltip>
        );
      }

      return (
        <BaseTooltip contents={row?.original?.user?.displayName ?? "-"}>
          <span>{row?.original?.user?.displayName || "-"}</span>
        </BaseTooltip>
      );
    },
  },
];

interface IProps {
  identifier: HistoryUpdateListParams["identifier"];
  // productId: string;
  // buildingId: string;
}

/* 
   상세/수정 > 변경내역
 */
const HistoryDetail = ({ identifier }: IProps) => {
  // 공간상품 상세 삭제 api
  const { executeAsync: getHistoryList } = useApiOperation(getHistoryListAsync);

  const { queryParams, queryParamsNavigate } = useQueryParams<HistoryUpdateListParams>({
    page: 0,
    size: 10,
    sort: {
      orders: [{ property: "revisionTime", direction: "DESC" }], // 초기 정렬 설정
    },
    identifier: identifier,
  });

  const { data: historyList } = useQuery({
    queryKey: ["product_historyList", queryParams],
    enabled: !!queryParams,
    queryFn: () => getHistoryList(queryParams),
  });

  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <div className="contents-container__wrap-contents">
            <BaseSectionTitle title={"변경내역"} />
            {historyList?.data?.data?.content.filter((data) => data.message.type) && (
              <BaseTable
                className="px0"
                data={historyList?.data?.data?.content.filter((data) => data.message.type) ?? []}
                columns={HistoryDetailColumns}
                currentSize={historyList.data.meta.pageMeta?.pageRequest.size || 0}
                pageIndex={historyList.data.meta.pageMeta?.pageRequest.page || 0}
                totalElements={historyList?.data?.meta.pageMeta?.totalElements || 0}
                totalPages={historyList?.data?.meta.pageMeta?.totalPages || 0}
                goPage={(page: number) => {
                  queryParamsNavigate({ ...queryParams, page: page });
                  // 페이지 이동 핸들러
                }}
                orders={queryParams.sort?.orders}
                setOrders={(orders?: Array<Order>) => {
                  queryParamsNavigate({ ...queryParams, sort: { orders } });
                }}
                disabledSortHeaders={["displayName", "category", "path", "setting", "userType", "message"]}
                sizeOption={(sizeValue: number) => {
                  queryParamsNavigate({ ...queryParams, size: sizeValue, page: 0 });
                  // 페이지 사이즈 변경 핸들러
                }}
              />
            )}
          </div>
        </div>
      </div>
      {/* 버튼영역 */}
      <div className="contents-container__btn-wrap">
        <div className="left-area d-flex">
          <GoToListButton />
        </div>
      </div>
    </>
  );
};

export default HistoryDetail;
